import { DS_GRADIENT } from '@calm/evexia-design-tokens';
import { Close } from '@mui/icons-material';
import {
    Box,
    Typography,
    Tooltip,
    styled,
    TooltipProps,
    tooltipClasses,
    IconButton,
} from '@rhg/libs/material';
import { ReactElement, useEffect, useState } from 'react';

const styleTooltipContainer = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'start',
    gap: '8px',

    '& svg': {
        height: '16px',
        width: '16px',
    },
    '& svg path': {
        fill: 'white',
    },
};

const styleIconButton = {
    padding: 0,
};

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        // TODO: These theme references don't work when the theme is changed. Design is fine with this for now, but it should be updated in the future.
        background:
            theme.palette.mode === 'light'
                ? DS_GRADIENT.LIGHT_ICON_PRIMARY
                : DS_GRADIENT.DARK_ICON_PRIMARY,
        maxWidth: '95dvw',

        '& > span.MuiTooltip-arrow': {
            color:
                theme.palette.mode === 'light'
                    ? DS_GRADIENT.LIGHT_ICON_PRIMARY
                    : DS_GRADIENT.DARK_ICON_PRIMARY,
        },
    },
}));

interface CHTooltipProps {
    isOpen?: boolean;
    // Needs to be 16x16, so remove the height and width props from the SVG file
    startIcon?: ReactElement;
    topLabel?: string;
    content: string;
    bottomLabel?: string;
    // Mui Tooltip expexts children to be a single ReactElement
    children: ReactElement<any, any>;
    arrowPlacement?: TooltipProps['placement'];
}

export const CHTooltip = ({
    startIcon,
    topLabel,
    content,
    bottomLabel,
    isOpen = true,
    children,
    arrowPlacement = 'top',
}: CHTooltipProps) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(isOpen);

    useEffect(() => {
        setIsTooltipOpen(isOpen);
    }, [isOpen]);

    return (
        <CustomTooltip
            open={isTooltipOpen}
            arrow
            placement={arrowPlacement}
            title={
                <Box sx={styleTooltipContainer}>
                    {startIcon}
                    <Box>
                        {topLabel && (
                            <Typography
                                variant="subtitle2"
                                component="p"
                                fontSize={10}
                                fontWeight={500}
                            >
                                {topLabel}
                            </Typography>
                        )}
                        <Typography variant="subtitle2" component="p">
                            {content}
                        </Typography>
                        {bottomLabel && (
                            <Typography
                                variant="subtitle2"
                                component="p"
                                fontSize={10}
                                fontWeight={500}
                            >
                                {bottomLabel}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        skipTracking
                        sx={styleIconButton}
                        onClick={() => setIsTooltipOpen(false)}
                    >
                        <Close />
                    </IconButton>
                </Box>
            }
        >
            {children}
        </CustomTooltip>
    );
};
